import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { Button, Flex, H3, SelectMenu } from '@decisiv/ui-components';
import Refresh from '@decisiv/iconix/lib/components/Refresh';
import ExecutionDetailsModal from '../../components/ExecutionDetails/ExecutionDetailsModal';
import { IntegrationDetailLocations } from './style';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import ExecutionsListTable from '../../components/ExecutionsListTable';
import DateTimeRangePicker from '../../components/DateTimeRangePicker';
import StepFunctionExecutionsApi from '../../api/step_function_executions';

import useRetryExecutionFunctions from '../../hooks/useRetryExecutionFunctions';
import {
  sourceOptions,
  statusOptions,
  triggerOptions,
} from '../../utils/constants';
import Label from '../../components/Label';
import localCache from '../../utils/localCache';
import { STORAGE_KEYS } from '../../utils/storageKeys';
import { dayStartWithHours } from '../../utils/dates';

export default function ExecutionsList({ integration }) {
  const itemsPerPage = 25;

  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [currentEndIndex, setCurrentEndIndex] = useState(0);
  const [executions, setExecutions] = useState([]);
  const [currentPageExecutions, setCurrentPageExecutions] = useState([]);
  const [totalExecutionsPage, setTotalExecutionsPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedExecution, setSelectedExecution] = useState();
  const [executionModalDetailsVisibility, setExecutionModalDetailsVisibility] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const {
    isRetryButtonVisible,
    isRetryButtonDisabled,
    handleRetry,
    retryExecutionIdRef,
  } = useRetryExecutionFunctions();
  const [filterSource, setFilterSource] = useState('ALL');
  const [filterTrigger, setFilterTrigger] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const pageChangeHandler = (newPageNumber) => {
    setCurrentPage(newPageNumber);
  };

  function hasCachedFilters() {
    return localCache.get(STORAGE_KEYS.EXECUTIONS_FILTER) !== null;
  }

  const initialSearch = hasCachedFilters()
    ? localCache.get(STORAGE_KEYS.EXECUTIONS_FILTER)
    : {};
  const [filters, setFilters] = useState(initialSearch);
  const [selectedDates, setSelectedDates] = useState();
  const DEFAULT_START_HOUR = '00';
  const DEFAULT_END_HOUR = '23';
  const [filterStartHour, setFilterStartHour] = useState(
    initialSearch.startHour || DEFAULT_START_HOUR,
  );
  const [filterEndHour, setFilterEndHour] = useState(
    initialSearch.endHour || DEFAULT_END_HOUR,
  );

  async function getExecutions(customFilters, page = currentPage) {
    setLoading(true);

    const responseLogs =
      await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByIntegrationId(
        {
          integrationId: integration.id,
          page,
          source: customFilters?.source,
          triggerType: customFilters?.triggerType,
          status: customFilters?.status,
          startDate: customFilters?.startDate
            ? dayStartWithHours(
                customFilters.startDate,
                customFilters.startHour ?? DEFAULT_START_HOUR,
              )
            : null,
          stopDate: customFilters?.stopDate
            ? dayStartWithHours(
                customFilters.stopDate,
                Number(customFilters.endHour ?? DEFAULT_END_HOUR) + 1,
              )
            : null,
        },
      );
    if (responseLogs.data) {
      setExecutions(responseLogs.data);

      const pageExecutions = responseLogs.data?.slice(0, 25);
      const startIndex = (page - 1) * itemsPerPage;
      setCurrentStartIndex(startIndex);

      const endIndex = startIndex + pageExecutions.length;
      setCurrentEndIndex(endIndex);
      setTotalRecords(responseLogs.totalRecords);
      setTotalExecutionsPage(
        Math.ceil(responseLogs.totalRecords / itemsPerPage),
      );
      setCurrentPageExecutions(pageExecutions);
    }
    setLoading(false);
  }

  // A filter change always restarts at page 1: the result set changes size, so
  // the previous offset can land past the end and render an empty page. The
  // fetch is left to the effect below, which keys on the filter state — the
  // cache write is only a convenience for the next visit and may silently
  // no-op (Safari private mode), so it must never be what a refetch reads.
  const applyFilters = (updatedFilter) => {
    setFilters(updatedFilter);
    localCache.set(STORAGE_KEYS.EXECUTIONS_FILTER, updatedFilter);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    localCache.remove(STORAGE_KEYS.EXECUTIONS_FILTER);
    setFilters({});

    // Reset the inputs
    setFilterSource('ALL');
    setFilterTrigger('ALL');
    setFilterStatus('ALL');
    setSelectedDates([undefined, undefined]);
    setFilterStartHour(DEFAULT_START_HOUR);
    setFilterEndHour(DEFAULT_END_HOUR);

    // The original passed the page number where the filters go, which read as
    // "no filters" by accident and left the offset on whatever page was open.
    // setFilters({}) above plus this land in one batch, so the fetch effect
    // runs once with the cleared filters on page 1.
    setCurrentPage(1);
  };

  // Restore the inputs from the cached filter set, once, on mount.
  useEffect(() => {
    const filter = initialSearch;

    setFilterSource(filter.source == null ? 'ALL' : filter.source);
    setFilterTrigger(filter.triggerType == null ? 'ALL' : filter.triggerType);
    setFilterStatus(filter.status == null ? 'ALL' : filter.status);
    setSelectedDates(
      filter.startDate == null
        ? [undefined, undefined]
        : [new Date(filter.startDate), new Date(filter.stopDate)],
    );
    setFilterStartHour(filter.startHour || DEFAULT_START_HOUR);
    setFilterEndHour(filter.endHour || DEFAULT_END_HOUR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The automatic fetch path; Refresh and the post-retry callback fetch directly.
  // Reads `filters`, never the cache, so a failed cache write can't serve stale
  // results. Keyed on integration.id: navigating swaps the prop without remount.
  useEffect(() => {
    if (!integration?.id) return;
    getExecutions(filters, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration?.id, currentPage, filters]);

  const handleChangeSource = (e) => {
    setFilterSource(e);

    let updatedFilter;

    if (e.length === 0 || e.includes('ALL')) {
      updatedFilter = {
        ...filters,
        source: null,
      };
    } else {
      updatedFilter = {
        ...filters,
        source: e,
      };
    }

    applyFilters(updatedFilter);
  };

  const handleChangeTrigger = (e) => {
    setFilterTrigger(e);

    let updatedFilter;

    if (e.length === 0 || e.includes('ALL')) {
      updatedFilter = {
        ...filters,
        triggerType: null,
      };
    } else {
      updatedFilter = {
        ...filters,
        triggerType: e,
      };
    }

    applyFilters(updatedFilter);
  };

  const handleChangeStatus = (e) => {
    setFilterStatus(e);

    let updatedFilter;

    if (e.length === 0 || e.includes('ALL')) {
      updatedFilter = {
        ...filters,
        status: null,
      };
    } else {
      updatedFilter = {
        ...filters,
        status: e,
      };
    }

    applyFilters(updatedFilter);
  };

  const applyDateTimeRange = (dates, startHour, endHour) => {
    setFilterStartHour(startHour);
    setFilterEndHour(endHour);

    const hasRange = dates && dates[0] && dates[1];
    setSelectedDates(hasRange ? dates : [undefined, undefined]);

    const updatedFilter = {
      ...filters,
      startDate: hasRange ? dates[0] : null,
      stopDate: hasRange ? dates[1] : null,
      startHour,
      endHour,
    };

    applyFilters(updatedFilter);
  };

  const clearDateTimeRange = () => {
    setSelectedDates([undefined, undefined]);
    setFilterStartHour(DEFAULT_START_HOUR);
    setFilterEndHour(DEFAULT_END_HOUR);

    const updatedFilter = {
      ...filters,
      startDate: null,
      stopDate: null,
      startHour: DEFAULT_START_HOUR,
      endHour: DEFAULT_END_HOUR,
    };

    applyFilters(updatedFilter);
  };

  const handleRefresh = () => {
    getExecutions(filters, currentPage);
  };

  return (
    <IntegrationDetailLocations
      textAlign="center"
      name="integrationDetailLocations"
    >
      <ExecutionDetailsModal
        log={selectedExecution}
        visibility={executionModalDetailsVisibility}
        handleVisibility={setExecutionModalDetailsVisibility}
        modalTitle={t`Execution Details`}
        logs={executions}
        onRefreshLogs={handleRefresh}
      />

      <Flex flexDirection="column" name="locationResult" flexGrow={1}>
        <Flex name="searchBar">
          <Flex
            marginTop={2}
            marginLeft={2}
            marginRight={2}
            name="topBar"
            flexGrow={1}
          >
            <Flex name="searchBox">
              <Flex>
                <H3 marginRight={2} marginTop={0.2}>
                  <Trans>Executions</Trans>
                </H3>
              </Flex>
            </Flex>
            <Flex justifyContent="flex-end" flexGrow={1}>
              <Button
                icon={Refresh}
                text={t`Refresh`}
                size="small"
                kind="secondary"
                onClick={handleRefresh}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex flexDirection="row" name="filters" marginLeft={2} marginTop={2}>
          <Flex>
            <SelectMenu
              label={t`Source:`}
              name="select-source"
              options={sourceOptions}
              value={filterSource}
              selected={filterSource}
              onChange={handleChangeSource}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2}>
            <SelectMenu
              label={t`Trigger:`}
              name="select-trigger"
              options={triggerOptions}
              value={filterTrigger}
              selected={filterTrigger}
              onChange={handleChangeTrigger}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2}>
            <SelectMenu
              label={t`Status:`}
              name="select-status"
              options={statusOptions}
              value={filterStatus}
              selected={filterStatus}
              onChange={handleChangeStatus}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2}>
            <Label shade={1}>{t`Date Range`}:</Label>
          </Flex>
          <Flex marginLeft={2}>
            <DateTimeRangePicker
              name="executions-date-range"
              values={selectedDates}
              startHour={filterStartHour}
              endHour={filterEndHour}
              onApply={applyDateTimeRange}
              onClear={clearDateTimeRange}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2} marginTop={0.5}>
            <Button
              text={t`Clear All`}
              size="small"
              variant="ghost"
              onClick={clearFilters}
            />
          </Flex>
        </Flex>
        <Flex name="searchResult">
          <WrapperWithLoading loading={loading}>
            <Flex flexGrow={1} flexDirection="column">
              <ExecutionsListTable
                currentPageExecutions={currentPageExecutions}
                currentStartIndex={currentStartIndex}
                currentEndIndex={currentEndIndex}
                itemsPerPage={itemsPerPage}
                executions={executions}
                totalPages={totalExecutionsPage}
                totalRecords={totalRecords}
                onPageChange={pageChangeHandler}
                currentPage={currentPage}
                setSelectedExecution={setSelectedExecution}
                isRetryButtonVisible={isRetryButtonVisible}
                isRetryButtonDisabled={isRetryButtonDisabled}
                handleRetry={handleRetry}
                retryExecutionIdRef={retryExecutionIdRef}
                setSortedExecutions={setCurrentPageExecutions}
                getExecutions={getExecutions}
                setExecutionModalDetailsVisibility={
                  setExecutionModalDetailsVisibility
                }
                tableColumns={[
                  'x_decisiv_trace_id',
                  'location_name',
                  'city',
                  'http_status',
                  'source',
                  'trigger_type',
                  'duration',
                  'started',
                  'end_time',
                  'actions',
                ]}
                maxRetryCount={integration.attributes.max_retries}
              />
            </Flex>
          </WrapperWithLoading>
        </Flex>
      </Flex>
    </IntegrationDetailLocations>
  );
}

ExecutionsList.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
